// Firebase web SDK: app + auth + App Check (reCAPTCHA Enterprise). Client-only —
// import from client components / useEffect, never during SSR/build prerender.

import { type FirebaseApp, getApps, initializeApp } from "firebase/app";
import { type Auth, getAuth } from "firebase/auth";

const config = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY!,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN!,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID!,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET!,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID!,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID!,
  measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
};

let _app: FirebaseApp | null = null;
let _appCheckInit = false;

export function firebaseApp(): FirebaseApp {
  if (_app) return _app;
  _app = getApps()[0] ?? initializeApp(config);
  return _app;
}

// Auth. Call only in the browser.
export function firebaseAuth(): Auth {
  return getAuth(firebaseApp());
}

// App Check with reCAPTCHA Enterprise. Idempotent; call once on client boot (layout
// useEffect). Dynamic import so app-check never enters the SSR bundle.
export async function initAppCheck(): Promise<void> {
  if (_appCheckInit || typeof window === "undefined") return;
  // No site key configured -> App Check is not in use for this project. Without
  // this guard the provider throws on an empty key and takes phone auth with it.
  if (!process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY) return;
  _appCheckInit = true;
  const { initializeAppCheck, ReCaptchaEnterpriseProvider } = await import(
    "firebase/app-check"
  );
  initializeAppCheck(firebaseApp(), {
    provider: new ReCaptchaEnterpriseProvider(
      process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!,
    ),
    isTokenAutoRefreshEnabled: true,
  });
}
